import { Box, Stack, Tooltip, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import BackHandIcon from '@mui/icons-material/BackHandOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import BrushIcon from '@mui/icons-material/BrushOutlined';
import PaletteIcon from '@mui/icons-material/PaletteOutlined';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import RedoIcon from '@mui/icons-material/RedoOutlined';
import { useSigilContext } from '../../context/sigilContext';
import { TOOLS } from '../../util/constants';
import { Eraser } from '../../assets/eraser';
import CircleIcon from '@mui/icons-material/CircleOutlined';
import SquareIcon from '@mui/icons-material/SquareOutlined';
import StarIcon from '@mui/icons-material/StarBorderOutlined';
import TriangleIcon from '@mui/icons-material/ChangeHistoryOutlined';
import CategoryIcon from '@mui/icons-material/CategoryOutlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';

const PaintTools = () => {

  const {
    tool,
    setTool,
    shapes,
    setShapes,
    setOpenColorTool,
    undo,
    redo,
    undoStack,
    redoStack,
    pushToHistory,
    i18n
  } = useSigilContext()

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectShape = (newTool) => {
    setTool(newTool);
    handleClose();
  };

  const shapeTools = [
    TOOLS.RECT,
    TOOLS.CIRCLE,
    TOOLS.STAR,
    TOOLS.TRIANGLE,
    TOOLS.LINE
  ];

  const isShapeToolSelected = shapeTools.includes(tool);

  const translate = i18n?.drawing || {};
  const selectedToolColor = '#42a5f5';

  const oldVersion = () => (
    <Box
      sx={{
        backgroundColor: 'rgb(31, 31, 31)',
        backdropFilter: 'blur(10px)',
        borderRadius: '30px',
        padding: '8px 16px',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
        width: 'auto',
        maxWidth: '95vw',
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <Stack
        direction='row'
        spacing={{ xs: 0.5, sm: 1, md: 2 }}
        alignItems="center"
        sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
      >
        {/* Pincel */}
        <Tooltip title={translate.pencilButton || 'Pincel'}>
          <Box
            onClick={() => setTool(TOOLS.PENCIL)}
            sx={{
              cursor: 'pointer',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              backgroundColor: tool === TOOLS.PENCIL ? selectedToolColor : 'transparent',
              '&:hover': { backgroundColor: tool === TOOLS.PENCIL ? selectedToolColor : 'rgba(255,255,255,0.1)' }
            }}
          >
            <BrushIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>

        {/* Paleta / Color */}
        <Tooltip title={translate.paletteButton || 'Color'}>
          <Box
            onClick={() => { setTool(TOOLS.PALETTE); setOpenColorTool(true) }}
            sx={{
              cursor: 'pointer',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              backgroundColor: tool === TOOLS.PALETTE ? selectedToolColor : 'transparent',
              '&:hover': { backgroundColor: tool === TOOLS.PALETTE ? selectedToolColor : 'rgba(255,255,255,0.1)' }
            }}
          >
            <PaletteIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>

        {/* Selector / Mover */}
        <Tooltip title={translate.selectorButton || 'Mover/Redimensionar'}>
          <Box
            onClick={() => setTool(TOOLS.SELECTOR)}
            sx={{
              cursor: 'pointer',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              backgroundColor: tool === TOOLS.SELECTOR ? selectedToolColor : 'transparent',
              '&:hover': { backgroundColor: tool === TOOLS.SELECTOR ? selectedToolColor : 'rgba(255,255,255,0.1)' }
            }}
          >
            <BackHandIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>

        {/* Menú de Formas */}
        <Tooltip title={translate.shapesButton || 'Formas'}>
          <Box
            onClick={handleClick}
            sx={{
              cursor: 'pointer',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              backgroundColor: isShapeToolSelected ? selectedToolColor : 'transparent',
              '&:hover': { backgroundColor: isShapeToolSelected ? selectedToolColor : 'rgba(255,255,255,0.1)' }
            }}
          >
            <CategoryIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              backgroundColor: 'rgb(31, 31, 31)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              color: 'white',
              borderRadius: '12px',
              mt: -1,
              '& .MuiMenuItem-root': {
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
            },
          }}
        >
          <MenuItem onClick={() => handleSelectShape(TOOLS.RECT)}>
            <SquareIcon sx={{ mr: 2 }} /> {translate.rectButton || 'Rectángulo'}
          </MenuItem>
          <MenuItem onClick={() => handleSelectShape(TOOLS.CIRCLE)}>
            <CircleIcon sx={{ mr: 2 }} /> {translate.circleButton || 'Círculo'}
          </MenuItem>
          <MenuItem onClick={() => handleSelectShape(TOOLS.STAR)}>
            <StarIcon sx={{ mr: 2 }} /> {translate.starButton || 'Estrella'}
          </MenuItem>
          <MenuItem onClick={() => handleSelectShape(TOOLS.TRIANGLE)}>
            <TriangleIcon sx={{ mr: 2 }} /> {translate.triangleButton || 'Triángulo'}
          </MenuItem>
          <MenuItem onClick={() => handleSelectShape(TOOLS.LINE)}>
            <HorizontalRuleIcon sx={{ mr: 2 }} /> {translate.lineButton || 'Línea Recta'}
          </MenuItem>
        </Menu>

        {/* Borrador */}
        <Tooltip title={translate.eraseButton || 'Borrador'}>
          <Box
            onClick={() => setTool(TOOLS.ERASE)}
            sx={{
              cursor: 'pointer',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              backgroundColor: tool === TOOLS.ERASE ? selectedToolColor : 'transparent',
              '&:hover': { backgroundColor: tool === TOOLS.ERASE ? selectedToolColor : 'rgba(255,255,255,0.1)' }
            }}
          >
            <Eraser sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>

        {/* Separador visual */}
        <Box sx={{ width: '1px', height: '24px', backgroundColor: 'rgba(255,255,255,0.2)', margin: '0 8px !important' }} />

        {/* Deshacer */}
        <Tooltip title={translate.undoButton || 'Deshacer'}>
          <Box
            onClick={() => undoStack.length > 0 && undo()}
            sx={{
              cursor: undoStack.length > 0 ? 'pointer' : 'default',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              opacity: undoStack.length > 0 ? 1 : 0.3,
              '&:hover': { backgroundColor: undoStack.length > 0 ? 'rgba(255,255,255,0.1)' : 'transparent' }
            }}
          >
            <UndoIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>

        {/* Rehacer */}
        <Tooltip title={translate.redoButton || 'Rehacer'}>
          <Box
            onClick={() => redoStack.length > 0 && redo()}
            sx={{
              cursor: redoStack.length > 0 ? 'pointer' : 'default',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              opacity: redoStack.length > 0 ? 1 : 0.3,
              '&:hover': { backgroundColor: redoStack.length > 0 ? 'rgba(255,255,255,0.1)' : 'transparent' }
            }}
          >
            <RedoIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>

        {/* Limpiar */}
        <Tooltip title={translate.clearButton || 'Limpiar'}>
          <Box
            onClick={() => {
              if (shapes.length > 0) {
                pushToHistory(shapes);
                setShapes([]);
              }
              setTool(TOOLS.CLEAR);
            }}
            sx={{
              cursor: 'pointer',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              backgroundColor: tool === TOOLS.CLEAR ? selectedToolColor : 'transparent',
              '&:hover': { backgroundColor: tool === TOOLS.CLEAR ? selectedToolColor : 'rgba(255,255,255,0.1)' }
            }}
          >
            <DeleteIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Tooltip>
      </Stack>
    </Box>
  )

  return oldVersion()


    ;
}

export default PaintTools;
